import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ApplicationConfig, inject, NgZone, provideAppInitializer } from '@angular/core';
import { PunchoutSessionInfo } from '@idealsupply/ngclient-webservice-cxml';
import { AuthenticationStore, INITIAL_USER, InjectAuthTokenInterceptor } from 'authentication-data';
import { AUTH_CONFIG, AuthenticationConfig } from 'config-data';
import { jwtDecode } from 'jwt-decode';
import { User, UserManager, UserProfile } from 'oidc-client-ts';
import { CXML_API_PATH, PUNCHOUT_JWT_BUYER_COOKIE_KEY, PUNCHOUT_JWT_RETURN_URL_KEY } from './constants';
import { InvalidSessionError } from './errors/InvalidSessionError';
import { MissingSessionError } from './errors/MissingSessionError';
import { defaultPunchoutSessionRenewalOptions, PunchoutSessionRenewalOptions } from './punchoutSessionRenewalOptions';
import { IS_PUNCHOUT } from './tokens';

const PUNCHOUT_SESSION_ID_KEY = 'punchout.session';
const PUNCHOUT_USER_KEY = 'punchout.returnUrl';

async function fetchPunchoutSession(sessionId: string, apiRoot: string): Promise<User> {
  const sessionInfoResponse = await fetch(`${apiRoot}${CXML_API_PATH}/punchout/session/${encodeURIComponent(sessionId)}`);
  if (!sessionInfoResponse.ok) {
    throw new InvalidSessionError(sessionId, sessionInfoResponse.status, sessionInfoResponse.statusText);
  }
  const sessionInfo = (await sessionInfoResponse.json()) as PunchoutSessionInfo;
  const jwt = jwtDecode<UserProfile>(sessionInfo.accessToken!);

  jwt[PUNCHOUT_JWT_BUYER_COOKIE_KEY] = sessionInfo.buyerCookie;
  jwt[PUNCHOUT_JWT_RETURN_URL_KEY] = sessionInfo.browserFormPostUrl;

  const user = new User({
    token_type: 'Bearer',
    access_token: sessionInfo.accessToken!,
    expires_at: jwt.exp,
    session_state: null,
    profile: jwt,
    scope: jwt['scope'] as string | undefined,
  });

  user.profile.family_name ||= 'User';
  user.profile.given_name ||= 'Punchout';
  user.profile.name ||= 'Punchout User';

  return user;
}

function startSessionRenewal(
  initialUser: User,
  sessionId: string,
  apiRoot: string,
  store: InstanceType<typeof AuthenticationStore>,
  options: PunchoutSessionRenewalOptions,
): void {
  const userKey = `${PUNCHOUT_USER_KEY}.${sessionId}`;
  let currentUserId = initialUser.profile.sub;

  const scheduleNextRenewal = (user: User) => {
    // Calculate delay based on token expiry
    const now = Date.now() / 1000; // Current time in seconds
    const expiresAt = user.expires_at ?? now + 3600; // Default to 1 hour if not set
    const delaySeconds = Math.max(0, expiresAt - now - options.renewalThresholdSeconds);
    const delayMs = delaySeconds * 1000;

    setTimeout(async () => {
      try {
        const newUser = await fetchPunchoutSession(sessionId, apiRoot);

        if (newUser.profile.sub !== currentUserId) {
          console.log('Punchout session user changed, reloading page...');
          window.location.reload();
          return;
        }

        sessionStorage.setItem(userKey, JSON.stringify(newUser));
        store.updateUserInfo(newUser);
        currentUserId = newUser.profile.sub;

        scheduleNextRenewal(newUser);
      } catch (error) {
        console.error('Failed to renew punchout session:', error);
        setTimeout(() => scheduleNextRenewal(user), options.renewalIntervalSeconds * 1000);
      }
    }, delayMs);
  };

  scheduleNextRenewal(initialUser);
}

export async function bootstrapAuthentication(
  authenticationConfig: AuthenticationConfig | undefined,
  applicationConfig: ApplicationConfig,
) {
  const urlParams = new URLSearchParams(window.location.search);
  let sessionId: string | null = urlParams.get('session');
  if (!sessionId) {
    sessionId = sessionStorage.getItem(PUNCHOUT_SESSION_ID_KEY);
  }
  if (!sessionId) {
    throw new MissingSessionError();
  }
  sessionStorage.setItem(PUNCHOUT_SESSION_ID_KEY, sessionId);
  const userKeyPrefix = `${PUNCHOUT_USER_KEY}.`;
  const userKey = `${userKeyPrefix}${sessionId}`;

  Object.keys(sessionStorage).forEach((key) => {
    if (key.startsWith(userKeyPrefix) && key !== userKey) {
      sessionStorage.removeItem(key);
    }
  });

  const apiRoot = authenticationConfig?.apiRoot || 'https://api.idealsupply.com';
  let user = JSON.parse(sessionStorage.getItem(userKey) || 'null');
  if (user) {
    user = new User(user);
  } else {
    user = await fetchPunchoutSession(sessionId, apiRoot);
    sessionStorage.setItem(userKey, JSON.stringify(user));
  }

  applicationConfig.providers.push(
    { provide: IS_PUNCHOUT, useValue: true },
    { provide: UserManager, useValue: null },
    { provide: AUTH_CONFIG, useValue: authenticationConfig },
    {
      provide: INITIAL_USER,
      useValue: user,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InjectAuthTokenInterceptor,
      multi: true,
    },
    provideAppInitializer(() => {
      // Start session renewal in browser only, outside Angular zone
      // to avoid blocking ApplicationRef.isStable() and hydration
      if (typeof window !== 'undefined') {
        const store = inject(AuthenticationStore);
        const ngZone = inject(NgZone);
        ngZone.runOutsideAngular(() => {
          startSessionRenewal(user, sessionId, apiRoot, store, defaultPunchoutSessionRenewalOptions);
        });
      }
    }),
  );
}
